// The API client seam. Mirrors the legacy CONTRACT (src/shared/platform/api-auth.ts) — absolute base,
// Bearer token from the `xlooop-workers` template — but NOT its window.Xcp* globals: the token getter is
// injected by the Clerk provider at boot. Writes carry a stable-per-action `Idempotency-Key` (§220-B).

import { API_BASE } from '../config';
import { ApiError, readEnvelope, type Enveloped } from './envelope';

type TokenGetter = () => Promise<string | null>;

// Set once by the auth provider (Clerk) at boot; MSW/dev leaves it returning null (fixtures ignore auth).
let tokenGetter: TokenGetter = async () => null;
export function setTokenGetter(getter: TokenGetter): void {
  tokenGetter = getter;
}

/** Prefix the absolute API base unless the path is already absolute. */
export function apiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return API_BASE + (path.startsWith('/') ? path : '/' + path);
}

/** A stable UUID per write action (the caller reuses it across retries of the SAME logical action). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

async function buildHeaders(opts?: { idempotencyKey?: string; json?: boolean }): Promise<HeadersInit> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts?.json !== false) headers['Content-Type'] = 'application/json';
  const token = await tokenGetter();
  if (token) headers['Authorization'] = 'Bearer ' + token;
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  return headers;
}

async function parse<T>(res: Response): Promise<Enveloped<T>> {
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; code?: string; request_id?: string };
    throw new ApiError(res.status, { error: b.error || `HTTP ${res.status}`, code: b.code, request_id: b.request_id });
  }
  return readEnvelope<T>(body);
}

export async function apiGet<T>(path: string, init?: RequestInit): Promise<Enveloped<T>> {
  const res = await fetch(apiUrl(path), { ...init, method: 'GET', headers: await buildHeaders() });
  return parse<T>(res);
}

/** POST/PATCH/PUT — WRITE. Pass an idempotencyKey (newIdempotencyKey()) so a retry can't double-write. */
export async function apiWrite<T>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  opts?: { idempotencyKey?: string },
): Promise<Enveloped<T>> {
  const res = await fetch(apiUrl(path), {
    method,
    headers: await buildHeaders({ idempotencyKey: opts?.idempotencyKey }),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return parse<T>(res);
}
